'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CalendarIcon, Clock, Globe, Loader2, CheckCircle } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

// Form validation function - will be created inside component to access translations
const createFormSchema = (t: any) => z.object({
  name: z.string().min(1, t('validation.required', { field: t('booking.fields.fullName') })),
  email: z.string().email(t('validation.email')),
  phone: z.string().min(1, t('validation.required', { field: t('booking.fields.phone') })),
  country: z.string().min(1, t('validation.required', { field: t('booking.fields.country') })),
  age: z.string()
    .min(1, t('validation.required', { field: t('booking.fields.age') }))
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 16 && num <= 100;
    }, 'Age must be between 16 and 100'),
  date: z.date({
    message: t('validation.required', { field: t('booking.fields.preferredDate') }),
  }),
  timeSlot: z.string().min(1, t('validation.required', { field: t('booking.fields.preferredTime') })),
  message: z.string().optional(),
});

type FormValues = {
  name: string;
  email: string;
  phone: string;
  country: string;
  age: string;
  date: Date;
  timeSlot: string;
  message?: string;
};

interface BookingFormProps {
  treatmentType: string;
  onSuccess?: () => void;
}

export default function BookingForm({ treatmentType, onSuccess }: BookingFormProps) {
  const { t, isRTL } = useTranslation('forms');
  const { t: tCountries } = useTranslation('countries');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const formSchema = createFormSchema(t);
  
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      country: '',
      age: '',
      timeSlot: '',
      message: '',
    }
  });

  const selectedCountry = watch('country') || '';
  const selectedTimeSlot = watch('timeSlot') || '';

  // Priority countries first, then alphabetical
  const priorityCountries = [
    'unitedStates', 'canada', 'unitedKingdom', 'germany', 'france', 'italy', 'spain',
    'netherlands', 'belgium', 'switzerland', 'austria', 'sweden', 'norway', 'denmark',
    'australia', 'newZealand', 'japan', 'southKorea', 'singapore', 'uae',
    'saudiArabia', 'kuwait', 'qatar', 'bahrain'
  ];
  
  const otherCountries = [
    'afghanistan', 'albania', 'algeria', 'andorra', 'angola', 'argentina', 'armenia', 'azerbaijan',
    'bangladesh', 'belarus', 'bolivia', 'bosniaHerzegovina', 'brazil', 'bulgaria', 'cambodia',
    'chile', 'china', 'colombia', 'costaRica', 'croatia', 'cuba', 'cyprus', 'czechRepublic',
    'ecuador', 'egypt', 'estonia', 'ethiopia', 'finland', 'georgia', 'ghana', 'greece',
    'guatemala', 'hungary', 'iceland', 'india', 'indonesia', 'iran', 'iraq', 'ireland',
    'israel', 'jamaica', 'jordan', 'kazakhstan', 'kenya', 'kosovo', 'latvia', 'lebanon',
    'libya', 'lithuania', 'luxembourg', 'macedonia', 'malaysia', 'malta', 'mexico', 'moldova',
    'monaco', 'mongolia', 'montenegro', 'morocco', 'myanmar', 'nepal', 'nicaragua', 'nigeria',
    'oman', 'pakistan', 'palestine', 'panama', 'paraguay', 'peru', 'philippines', 'poland',
    'portugal', 'romania', 'russia', 'serbia', 'slovakia', 'slovenia', 'southAfrica',
    'sriLanka', 'sudan', 'syria', 'taiwan', 'thailand', 'tunisia', 'turkey',
    'uganda', 'ukraine', 'uruguay', 'uzbekistan', 'venezuela', 'vietnam', 'yemen', 'zambia',
    'zimbabwe'
  ];
  
  const countryKeys = [...priorityCountries, ...otherCountries];
  
  const countries = countryKeys.map(key => ({
    key,
    name: tCountries(key)
  }));

  // Time slots
  const timeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM',
    '05:00 PM', '05:30 PM'
  ];

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      // Split name into first and last name
      const nameParts = data.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email: data.email,
          phone: data.phone,
          age: parseInt(data.age),
          country: data.country,
          appointmentType: 'consultation',
          treatments: [treatmentType],
          description: data.message || '',
          preferredDate: data.date.toISOString(),
          preferredTime: data.timeSlot,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setIsSuccess(true);

        // Show appropriate success message based on response
        if (result.message.includes('development mode')) {
          console.log('✅ Booking submitted successfully in development mode');
        } else {
          console.log('✅ Booking email sent successfully');
        }

        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorData = await response.json();
        console.error('Booking failed:', errorData);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewBooking = () => {
    setIsSuccess(false);
    reset({
      name: '',
      email: '',
      phone: '',
      country: '',
      age: '',
      timeSlot: '',
      message: '',
    });
    setSelectedDate(null);
  };

  if (isSuccess) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className={`text-xl font-bold text-navy dark:text-white mb-2 ${isRTL ? 'text-right' : ''}`}>
          {t('messages.success.consultation')}
        </h3>
        <p className={`text-slate-600 dark:text-slate-300 mb-6 ${isRTL ? 'text-right' : ''}`}>
          {t('booking.successMessage')}
        </p>
        <Button
          onClick={handleNewBooking}
          className="bg-gold hover:bg-gold-600 text-navy font-semibold"
        >
          {t('booking.buttons.bookConsultation')}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Name and Email Row */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Input
            {...register('name')}
            placeholder={t('booking.fields.fullName')}
            className={`h-12 rounded-xl border-2 transition-all duration-300 bg-white dark:bg-slate-800 text-navy dark:text-white placeholder:text-slate-600 dark:placeholder:text-slate-300 ${isRTL ? 'text-right' : ''} ${errors.name
              ? 'border-red-500 focus:border-red-500'
              : 'border-slate-200 dark:border-slate-700 focus:border-gold hover:border-gold/50'
              }`}
          />
          {errors.name && (
            <p className={`text-red-500 text-sm mt-2 flex items-center ${isRTL ? 'space-x-reverse flex-row-reverse' : 'space-x-1'}`}>
              <AlertTriangle className="w-4 h-4" />
              <span>{errors.name.message}</span>
            </p>
          )}
        </div>

        <div>
          <Input
            {...register('email')}
            type="email"
            placeholder={t('booking.fields.email')}
            className={`h-12 rounded-xl border-2 transition-all duration-300 bg-white dark:bg-slate-800 text-navy dark:text-white placeholder:text-slate-600 dark:placeholder:text-slate-300 ${isRTL ? 'text-right' : ''} ${errors.email
              ? 'border-red-500 focus:border-red-500'
              : 'border-slate-200 dark:border-slate-700 focus:border-gold hover:border-gold/50'
              }`}
          />
          {errors.email && (
            <p className={`text-red-500 text-sm mt-2 flex items-center ${isRTL ? 'space-x-reverse flex-row-reverse' : 'space-x-1'}`}>
              <AlertTriangle className="w-4 h-4" />
              <span>{errors.email.message}</span>
            </p>
          )}
        </div>
      </div>

      {/* Phone and Country Row */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Input
            {...register('phone')}
            placeholder={t('booking.fields.phone')}
            className={`h-12 rounded-xl border-2 transition-all duration-300 bg-white dark:bg-slate-800 text-navy dark:text-white placeholder:text-slate-600 dark:placeholder:text-slate-300 ${isRTL ? 'text-right' : ''} ${errors.phone
              ? 'border-red-500 focus:border-red-500'
              : 'border-slate-200 dark:border-slate-700 focus:border-gold hover:border-gold/50'
              }`}
          />
          {errors.phone && (
            <p className={`text-red-500 text-sm mt-2 flex items-center ${isRTL ? 'space-x-reverse flex-row-reverse' : 'space-x-1'}`}>
              <AlertTriangle className="w-4 h-4" />
              <span>{errors.phone.message}</span>
            </p>
          )}
        </div>

        <div>
          <Select value={selectedCountry} onValueChange={(value) => setValue('country', value, { shouldValidate: true })}>
            <SelectTrigger className={`h-12 rounded-xl border-2 transition-all duration-300 bg-white dark:bg-slate-800 text-navy dark:text-white ${isRTL ? 'flex-row-reverse' : ''} ${errors.country
              ? 'border-red-500 focus:border-red-500'
              : 'border-slate-200 dark:border-slate-700 focus:border-gold hover:border-gold/50'
              }`}>
              <Globe className={`w-5 h-5 text-gold ${isRTL ? 'ml-2' : 'mr-2'}`} />
              <SelectValue placeholder={t('booking.placeholders.selectCountry')} className={`${isRTL ? 'text-right' : 'text-left'} text-slate-600 dark:text-slate-300`} />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              {countries.map((country) => (
                <SelectItem key={country.key} value={country.name} className="text-navy dark:text-white">
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.country && (
            <p className={`text-red-500 text-sm mt-2 flex items-center ${isRTL ? 'space-x-reverse flex-row-reverse' : 'space-x-1'}`}>
              <AlertTriangle className="w-4 h-4" />
              <span>{errors.country.message}</span>
            </p>
          )}
        </div>
      </div>

      {/* Age */}
      <div>
        <Input
          {...register('age')}
          type="number"
          min="16"
          max="100"
          placeholder={t('booking.fields.age')}
          className={`h-12 rounded-xl border-2 transition-all duration-300 bg-white dark:bg-slate-800 text-navy dark:text-white placeholder:text-slate-600 dark:placeholder:text-slate-300 ${isRTL ? 'text-right' : ''} ${errors.age
            ? 'border-red-500 focus:border-red-500'
            : 'border-slate-200 dark:border-slate-700 focus:border-gold hover:border-gold/50'
            }`}
        />
        {errors.age && (
          <p className={`text-red-500 text-sm mt-2 flex items-center ${isRTL ? 'space-x-reverse flex-row-reverse' : 'space-x-1'}`}>
            <AlertTriangle className="w-4 h-4" />
            <span>{errors.age.message}</span>
          </p>
        )}
      </div>

      {/* Date and Time Row */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="relative w-full">
            <CalendarIcon className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 pointer-events-none w-5 h-5 text-gold z-10`} />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    `w-full h-12 ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4 text-left'} justify-start font-normal rounded-xl border-2 transition-all duration-300 bg-white dark:bg-slate-800 text-navy dark:text-white`,
                    !selectedDate && "text-slate-600 dark:text-slate-300",
                    errors.date
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-slate-200 dark:border-slate-700 focus:border-gold hover:border-gold/50'
                  )}
                >
                  {selectedDate ? format(selectedDate, "MMMM d, yyyy") : <span>{t('booking.fields.preferredDate')}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate || undefined}
                  onSelect={(date) => {
                    setSelectedDate(date || null);
                    if (date) {
                      setValue('date', date, { shouldValidate: true });
                    }
                  }}
                  disabled={(date) => 
                    date < new Date() || date.getDay() === 0
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          {errors.date && (
            <p className={`text-red-500 text-sm mt-2 flex items-center ${isRTL ? 'space-x-reverse flex-row-reverse' : 'space-x-1'}`}>
              <AlertTriangle className="w-4 h-4" />
              <span>{errors.date.message}</span>
            </p>
          )}
        </div>

        <div>
          <Select value={selectedTimeSlot} onValueChange={(value) => setValue('timeSlot', value, { shouldValidate: true })}>
            <SelectTrigger className={`h-12 rounded-xl border-2 transition-all duration-300 bg-white dark:bg-slate-800 text-navy dark:text-white ${isRTL ? 'flex-row-reverse' : ''} ${errors.timeSlot
              ? 'border-red-500 focus:border-red-500'
              : 'border-slate-200 dark:border-slate-700 focus:border-gold hover:border-gold/50'
              }`}>
              <Clock className={`w-5 h-5 text-gold ${isRTL ? 'ml-2' : 'mr-2'}`} />
              <SelectValue placeholder={t('booking.placeholders.selectPreferredTime')} className={`${isRTL ? 'text-right' : 'text-left'} text-slate-600 dark:text-slate-300`} />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 max-h-80 z-50 min-w-[290px] w-auto">
              <div className="grid grid-cols-2 gap-1 p-1">
                {timeSlots.map((slot) => (
                  <SelectItem key={slot} value={slot} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 text-navy dark:text-white text-center py-1 px-2 rounded">
                    {slot}
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
          {errors.timeSlot && (
            <p className={`text-red-500 text-sm mt-2 flex items-center ${isRTL ? 'space-x-reverse flex-row-reverse' : 'space-x-1'}`}>
              <AlertTriangle className="w-4 h-4" />
              <span>{errors.timeSlot.message}</span>
            </p>
          )}
        </div>
      </div>

      {/* Message */}
      <div>
        <Textarea
          {...register('message')}
          placeholder={t('booking.placeholders.additionalMessage')}
          rows={4}
          className={`rounded-xl border-2 transition-all duration-300 bg-white dark:bg-slate-800 text-navy dark:text-white placeholder:text-slate-600 dark:placeholder:text-slate-300 border-slate-200 dark:border-slate-700 focus:border-gold hover:border-gold/50 resize-none ${isRTL ? 'text-right' : ''}`}
        />
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <Button
          type="submit"
          className="w-full h-12 bg-gold hover:bg-gold-600 text-navy font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className={`h-5 w-5 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('booking.buttons.booking')}
            </>
          ) : (
            <>
              <CalendarIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('booking.buttons.bookConsultation')}
            </>
          )}
        </Button>
      </div>

      {/* Trust Indicators */}
      <div className="text-center text-sm text-slate-600 dark:text-slate-300 space-y-1 pt-2">
        <p className="flex items-center justify-center gap-2">
          <CheckCircle className="w-4 h-4 text-gold" />
          <span>{t('booking.trustIndicators.freeConsultation')}</span>
        </p>
        <p className="flex items-center justify-center gap-2">
          <CheckCircle className="w-4 h-4 text-gold" />
          <span>{t('booking.trustIndicators.callBack')}</span>
        </p>
      </div>
    </form>
  );
}
